import React from 'react';
import { Grid, Spin, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { QuickAdjustPlanogram as Planogram } from '../../types';
import {
    formatCents,
    formatDueDate,
    getDueDateLevel,
    getStatusColorLevel,
    getStatusLabel,
} from '../../utils';
import ProductAvatar from '../ProductAvatar';
import { LoadingMore } from '../../styles';
import * as S from './styles';

const LOAD_MORE_THRESHOLD = 150;

type ProductTableProps = {
    planograms: Planogram[];
    onEdit: (planogram: Planogram) => void;
    onEndReached: () => void;
    isFetchingNextPage: boolean;
};

const getColumns = (
    onEdit: (planogram: Planogram) => void,
    isMobile: boolean,
): TableColumnsType<Planogram> => [
    {
        title: 'Produto',
        key: 'product',
        width: 280,
        fixed: isMobile ? undefined : 'left',
        className: S.FIRST_COLUMN_CLASS,
        render: (_, { product }) => (
            <S.ProductCell>
                <ProductAvatar product={product} />
                <S.ProductInfo>
                    <S.ProductName>{product.name}</S.ProductName>
                    <S.Ean>
                        {product.BarCodes?.[0]?.code || 'Sem código de barras'}
                    </S.Ean>
                </S.ProductInfo>
            </S.ProductCell>
        ),
    },
    {
        title: 'Categoria',
        key: 'category',
        width: 160,
        render: (_, { product }) => (
            <S.CategoryLabel>
                {product.Category?.name ?? 'Sem categoria'}
            </S.CategoryLabel>
        ),
    },
    {
        title: 'Cód. planog.',
        key: 'planogram_code',
        width: 120,
        render: (_, planogram) => (
            <S.MonoValue>{planogram.planogram_code || '—'}</S.MonoValue>
        ),
    },
    {
        title: 'Status',
        key: 'status',
        width: 230,
        render: (_, planogram) => (
            <S.StatusBadge level={getStatusColorLevel(planogram.status)}>
                {getStatusLabel(planogram.status)}
            </S.StatusBadge>
        ),
    },
    {
        title: 'Qtd. Padrão',
        key: 'standard_amount',
        width: 110,
        align: 'right',
        render: (_, planogram) => (
            <S.Value>{planogram.standard_amount}</S.Value>
        ),
    },
    {
        title: 'Nível Alerta',
        key: 'alert_level',
        width: 110,
        align: 'right',
        render: (_, planogram) => <S.Value>{planogram.alert_level}</S.Value>,
    },
    {
        title: 'Estoque atual',
        key: 'stock_amount',
        width: 120,
        align: 'right',
        render: (_, planogram) => (
            <S.Value danger={planogram.stock_amount <= planogram.alert_level}>
                {planogram.stock_amount}
            </S.Value>
        ),
    },
    {
        title: 'Alerta de vencimento',
        key: 'due_date',
        width: 170,
        align: 'right',
        render: (_, planogram) => (
            <S.DueDateValue level={getDueDateLevel(planogram.due_date)}>
                {formatDueDate(planogram.due_date) ?? '—'}
            </S.DueDateValue>
        ),
    },
    {
        title: 'Custo un.',
        key: 'last_price',
        width: 110,
        align: 'right',
        render: (_, { product }) => (
            <S.Value>{formatCents(product.last_price_in_cents)}</S.Value>
        ),
    },
    {
        title: 'Preço lista',
        key: 'price_list',
        width: 110,
        align: 'right',
        render: (_, planogram) => (
            <S.Value strong>
                {formatCents(planogram.price_list_in_cents)}
            </S.Value>
        ),
    },
    {
        title: 'Preço App',
        key: 'price_final',
        width: 110,
        align: 'right',
        render: (_, planogram) => (
            <S.AppPriceValue>
                {formatCents(planogram.price_final_in_cents)}
            </S.AppPriceValue>
        ),
    },
    {
        title: 'Ações',
        key: 'actions',
        width: 100,
        align: 'right',
        className: S.LAST_COLUMN_CLASS,
        render: (_, planogram) => (
            <S.EditButton type="button" onClick={() => onEdit(planogram)}>
                Editar
            </S.EditButton>
        ),
    },
];

export const ProductTable = ({
    planograms,
    onEdit,
    onEndReached,
    isFetchingNextPage,
}: ProductTableProps) => {
    const { lg } = Grid.useBreakpoint();
    const isMobile = !lg;
    const columns = React.useMemo(
        () => getColumns(onEdit, isMobile),
        [onEdit, isMobile],
    );
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD) {
            onEndReached();
        }
    };

    return (
        <S.Wrapper>
            <Table<Planogram>
                rowKey="id"
                columns={columns}
                dataSource={planograms}
                pagination={false}
                scroll={{
                    // max-content uses auto layout, so the product column grows to fit the full name
                    x: 'max-content',
                    y: S.TABLE_MAX_HEIGHT - S.TABLE_HEADER_HEIGHT,
                }}
                onScroll={handleScroll}
                summary={() =>
                    isFetchingNextPage && (
                        <Table.Summary.Row>
                            <Table.Summary.Cell
                                index={0}
                                colSpan={columns.length}
                            >
                                <LoadingMore>
                                    <Spin size="small" /> Carregando mais
                                    produtos...
                                </LoadingMore>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    )
                }
            />
        </S.Wrapper>
    );
};

export default ProductTable;
